import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { tokens, type ThemeTokens } from './tokens';

interface ThemeContextValue {
  theme: ThemeTokens;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: tokens });

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme] = useState<ThemeTokens>(tokens);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-bg', theme.colors.background);
    root.style.setProperty('--color-surface', theme.colors.surface);
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-primary-rgb', '99, 102, 241');
    root.style.setProperty('--color-primary-muted', theme.colors.primaryMuted);
    root.style.setProperty('--color-primary-subtle', 'rgba(99,102,241,0.06)');
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-text', theme.colors.text);
    root.style.setProperty('--color-text-muted', theme.colors.textMuted);
    root.style.setProperty('--color-text-dim', theme.colors.textDim);
    root.style.setProperty('--color-success', theme.colors.success);
    root.style.setProperty('--color-danger', theme.colors.danger);
    root.style.setProperty('--color-info', theme.colors.info);
    root.style.setProperty('--color-warning', theme.colors.warning);
    root.style.setProperty('--font-heading', theme.fonts.heading);
    root.style.setProperty('--font-body', theme.fonts.body);
    root.style.setProperty('--font-mono', theme.fonts.mono);
    root.style.setProperty('--glass-bg', theme.glass.background);
    root.style.setProperty('--glass-border', theme.glass.border);
    root.style.setProperty('--glass-blur', theme.glass.blur);
    root.style.setProperty('--shadow-glow', theme.shadows.glow);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme }}>
      {children}
    </ThemeContext.Provider>
  );
}
